import React, { useState } from "react";
import { RotateCcw, AlertTriangle } from "lucide-react";
import { Modal } from "./Modal";
import { STATUS } from "./Badges";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

// Same set of manually-selectable statuses as the manager "Correct status"
// tool — excludes closed/paid, which must go through the proper settlement
// flow rather than being picked directly.
const REOPEN_STATUS_OPTIONS = Object.keys(STATUS).filter((s) => !["closed", "paid"].includes(s));

/**
 * Manager/Admin only. Re-opens (re-instates) ONE closed/paid equipment line
 * on a job — without touching any of the job's other lines — so a
 * mistakenly-closed DN can be corrected one line at a time. The job's
 * overall "Closed" status is computed live from its lines, so re-opening
 * this one line automatically takes the whole DN off "Closed" too.
 */
export function ReopenLineModal({ item, onClose, onReopened }) {
  const [targetStatus, setTargetStatus] = useState("ready_for_admin");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!note.trim()) { toast.error("Please add a note explaining why this line is being re-opened."); return; }
    setBusy(true);
    try {
      await api.post(`/job-items/${item.id}/reopen`, { target_status: targetStatus, note: note.trim() });
      toast.success(`Line re-opened — now "${STATUS[targetStatus]?.[0] || targetStatus}"`);
      onReopened && onReopened();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={RotateCcw}
      title="Re-open equipment line"
      subtitle={`${item.description || ""} · ${item.serial_number ? "SN " + item.serial_number : item.issue_number ? "Issue " + item.issue_number : "—"} · currently ${STATUS[item.status]?.[0] || item.status}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="reopen-line-cancel">Cancel</button>
        <button className="btn gold" onClick={confirm} disabled={busy} data-testid="reopen-line-confirm">
          <RotateCcw size={13} /> {busy ? "Re-opening…" : "Re-open this line"}
        </button>
      </>}>

      <div className="notice" style={{ marginBottom: 12 }}>
        <AlertTriangle size={14} />
        <span>
          This re-opens only this ONE line — every other line on this DN stays exactly as it is.
          The job's overall status will automatically reflect "not closed" again once this line moves.
        </span>
      </div>

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Move this line back to
      </label>
      <select className="line-action-select" value={targetStatus}
        onChange={(e) => setTargetStatus(e.target.value)}
        data-testid="reopen-line-status-select"
        style={{ width: "100%", marginBottom: 12 }}>
        {REOPEN_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS[s][0]}</option>)}
      </select>

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Reason (required — kept in the line's status history for audit)
      </label>
      <input type="text" placeholder="Why is this line being re-opened?"
        value={note} onChange={(e) => setNote(e.target.value)}
        data-testid="reopen-line-note"
        style={{ width: "100%" }} />

      {["dispatched", "delivered", "collected"].includes(targetStatus) && (
        <p className="t-mut" style={{ fontSize: 11.5, marginTop: 10 }}>
          Note: if payment on this line is already marked "Paid", it may auto-close again once handed
          over. Pick a working status (e.g. Ready for Admin, In Progress) if you need it to stay open.
        </p>
      )}
    </Modal>
  );
}
